import createApiClient from './client'

/** The multitenant CRM backend (CREDINTEL-SERVICE, context /executions behind the gateway). */
const crmClient = createApiClient('/executions')

// Tenant-scoped endpoints need X-Tenant-Id; /platform and /me ignore it
crmClient.interceptors.request.use((config) => {
  const tenant = JSON.parse(localStorage.getItem('tenant') || 'null')
  if (tenant?.tenantId) {
    config.headers['X-Tenant-Id'] = tenant.tenantId
  }
  return config
})

export const SUBSCRIPTION_EVENT = 'credintel:subscription'

// A read-only or locked workspace (subscription ended): let the app refresh its banner
crmClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const code = error?.response?.data?.code
    if (code === 'SUBSCRIPTION_READ_ONLY' || code === 'SUBSCRIPTION_LOCKED') {
      window.dispatchEvent(new Event(SUBSCRIPTION_EVENT))
    }
    return Promise.reject(error)
  }
)

const data = (promise) => promise.then((response) => response.data)
const blob = (promise) => promise.then((response) => response.data)

export const meAPI = {
  tenants: () => data(crmClient.get('/services/me/tenants')),
  /** name: optional display name, used if they don't have one yet */
  acceptInvite: (token, name) => data(crmClient.post('/services/me/invites/accept', { token, name: name || undefined })),
  /**
   * → { userId, email, displayName (null until set), pictureUrl (from a Google / Microsoft sign-in; null when none or hidden),
   * photoHidden }
   */
  profile: () => data(crmClient.get('/services/me/profile')),
  updateProfile: (displayName) => data(crmClient.put('/services/me/profile', { displayName })),
  /** hidden: don't show the sign-in photo to anyone */
  setPhotoHidden: (hidden) => data(crmClient.put('/services/me/profile/photo', { hidden })),
  /** What this installation offers → { emailEnabled, maxFileSizeMb } */
  features: () => data(crmClient.get('/services/me/features')),
  /** Starts a workspace of your own on a free trial (when sign-up is open) → { tenantId, name, status, role } */
  createWorkspace: (name) => data(crmClient.post('/services/me/workspaces', { name })),
  /** The Monday summary email → { weekly } */
  digest: () => data(crmClient.get('/services/me/digest')),
  setDigest: (weekly) => data(crmClient.put('/services/me/digest', { weekly })),
}

/** No sign-in needed: the invite token is the credential. */
export const publicInvitesAPI = {
  preview: (token) => data(crmClient.get(`/public/invites/${encodeURIComponent(token)}`)),
  accept: (token, password, name) => data(crmClient.post(`/public/invites/${encodeURIComponent(token)}/accept`, { password, name: name || undefined })),
}

export const invitesAPI = {
  list: () => data(crmClient.get('/services/tenant/invites')),
  /** → { invite, inviteUrl, emailSent, emailDelivery: SENT | FAILED | OFF } */
  create: (body) => data(crmClient.post('/services/tenant/invites', body)),
  regenerate: (inviteId) => data(crmClient.post(`/services/tenant/invites/${inviteId}/regenerate`)),
  revoke: (inviteId) => data(crmClient.delete(`/services/tenant/invites/${inviteId}`)),
}

export const platformAPI = {
  listTenants: () => data(crmClient.get('/services/platform/tenants')),
  /** → { tenant, adminInvite } (adminInvite only when the admin isn't the caller) */
  createTenant: (body) => data(crmClient.post('/services/platform/tenants', body)),
  updateStatus: (tenantId, status) => data(crmClient.patch(`/services/platform/tenants/${tenantId}/status`, { status })),
  /** Permanently deletes a switched-off workspace; confirmName = its exact name. */
  deleteTenant: (tenantId, confirmName) => data(crmClient.delete(`/services/platform/tenants/${tenantId}`, { data: { confirmName } })),
}

export const billingAPI = {
  /**
   * → { billingEnabled, planCode, planName, status, phase (TRIAL | ACTIVE | GRACE | READ_ONLY | LOCKED), phaseEndsAt,
   * paidUntil, interval, currency, extraSeats, limits, features, usage (admins only), canManage }
   */
  subscription: () => data(crmClient.get('/services/billing/subscription')),
  /** Plans on offer, prices in minor units: { INR: { MONTH, YEAR, SEAT_MONTH, SEAT_YEAR }, USD: {…} } */
  plans: () => data(crmClient.get('/services/billing/plans')),
  /**
   * What choosing this would do → { amount (charged = netAmount + taxAmount), taxRateBp, supply (INTRA | INTER | EXPORT | null),
   * provider, mode: NOW | LATER | CHANGE, firstChargeAt, credit, reason: TRIAL | UNUSED_TIME | COUPON, couponCode, couponValue }
   * (LATER: first charge after the trial / credit / coupon; CHANGE: the live subscription changes now, difference charged).
   * 409 code BILLING_DETAILS_NEEDED: GST invoices are on and the workspace has no billing details yet.
   */
  quote: (body) => data(crmClient.post('/services/billing/checkouts/quote', body)),
  /** body: { planCode, interval, currency, extraSeats, couponCode? } → { id, status, kind: NEW | CHANGE, redirectUrl (NEW), … } */
  checkout: (body) => data(crmClient.post('/services/billing/checkouts', body)),
  /** status: PENDING until the provider confirms, then COMPLETED (or FAILED) */
  getCheckout: (id) => data(crmClient.get(`/services/billing/checkouts/${id}`)),
  payments: () => data(crmClient.get('/services/billing/payments')),
  /** No more renewals; works until the paid period ends */
  cancel: () => data(crmClient.post('/services/billing/cancel')),
  /** Who invoices are made out to → { profile (null until saved), invoicing, pricesIncludeGst, gstRateBp, sellerStateCode, states } */
  profile: () => data(crmClient.get('/services/billing/profile')),
  saveProfile: (body) => data(crmClient.put('/services/billing/profile', body)),
  /** GST invoices and credit notes, newest first */
  invoices: () => data(crmClient.get('/services/billing/invoices')),
  invoicePdf: (id) => blob(crmClient.get(`/services/billing/invoices/${id}/pdf`, { responseType: 'blob' })),
}

/** Self-serve sign-up; no sign-in. The token comes from the email link's #fragment. */
export const signupAPI = {
  /** → { enabled, emailSignup } */
  config: () => data(crmClient.get('/public/signup/config')),
  start: (body) => data(crmClient.post('/public/signup', body)),
  preview: (token) => data(crmClient.post('/public/signup/preview', { token })),
  /** → { tenantId, workspaceName, email } */
  complete: (token, password, workspaceName) => data(crmClient.post('/public/signup/complete', { token, password, workspaceName })),
}

/** Platform admins: every workspace's subscription, and the plans. */
export const platformBillingAPI = {
  subscriptions: () => data(crmClient.get('/services/platform/billing/subscriptions')),
  /** body: { planCode, status, interval, currency, extraSeats, paidUntil (null = no end), note } */
  updateSubscription: (tenantId, body) => data(crmClient.put(`/services/platform/billing/subscriptions/${tenantId}`, body)),
  plans: () => data(crmClient.get('/services/platform/billing/plans')),
  savePlan: (code, body) => data(crmClient.put(`/services/platform/billing/plans/${encodeURIComponent(code)}`, body)),
  /** MRR per currency and plan, conversion, trials ending, at risk, recently lost */
  overview: () => data(crmClient.get('/services/platform/billing/overview')),
  /** Sends billing notices that are due now (each still goes out once) */
  runNotices: () => data(crmClient.post('/services/platform/billing/notices/run')),
  /** Asks the payment providers how each online subscription stands; → corrections made [{ workspace, provider, what, from, to }] */
  reconcile: () => data(crmClient.post('/services/platform/billing/reconcile')),
  /** Logs an error on purpose to check the alert email / chat message (409 when alerts are off) */
  testAlert: () => data(crmClient.post('/services/platform/ops/test-alert')),
  /** The seller's GST details → { seller, enabled, states }; a GSTIN with name, address and state switches invoices on */
  invoiceSettings: () => data(crmClient.get('/services/platform/billing/invoice-settings')),
  saveInvoiceSettings: (body) => data(crmClient.put('/services/platform/billing/invoice-settings', body)),
  invoices: () => data(crmClient.get('/services/platform/billing/invoices')),
  invoicePdf: (id) => blob(crmClient.get(`/services/platform/billing/invoices/${id}/pdf`, { responseType: 'blob' })),
  /** body: { amount (total incl. GST, minor units), reason } */
  creditNote: (id, body) => data(crmClient.post(`/services/platform/billing/invoices/${id}/credit-notes`, body)),
  coupons: () => data(crmClient.get('/services/platform/billing/coupons')),
  /** body: { description, percentOff | amountOff + currency, durationMonths, expiresAt, maxRedemptions, active } */
  saveCoupon: (code, body) => data(crmClient.put(`/services/platform/billing/coupons/${encodeURIComponent(code)}`, body)),
}

export const membersAPI = {
  list: () => data(crmClient.get('/services/tenant/members')),
  update: (memberId, body) => data(crmClient.patch(`/services/tenant/members/${memberId}`, body)),
  remove: (memberId) => data(crmClient.delete(`/services/tenant/members/${memberId}`)),
  /** Emails the member a password reset link (admins; 409 when email is off) */
  sendPasswordReset: (memberId) => data(crmClient.post(`/services/tenant/members/${memberId}/password-reset`)),
}

export const formsAPI = {
  fieldTypes: () => data(crmClient.get('/services/forms/field-types')),
  list: (params) => data(crmClient.get('/services/forms', { params })),
  get: (formId) => data(crmClient.get(`/services/forms/${formId}`)),
  published: (code) => data(crmClient.get(`/services/forms/code/${encodeURIComponent(code)}/published`)),
  versions: (code) => data(crmClient.get(`/services/forms/code/${encodeURIComponent(code)}/versions`)),
  create: (body) => data(crmClient.post('/services/forms', body)),
  update: (formId, body) => data(crmClient.put(`/services/forms/${formId}`, body)),
  publish: (formId) => data(crmClient.post(`/services/forms/${formId}/publish`)),
  archive: (formId) => data(crmClient.post(`/services/forms/${formId}/archive`)),
  newVersion: (formId) => data(crmClient.post(`/services/forms/${formId}/versions`)),
  remove: (formId) => data(crmClient.delete(`/services/forms/${formId}`)),
}

/** Query params shared by the list and the export: filters → filter.key / filter.key.op */
const listParams = ({ entityType, sort = 'updatedOn,desc', q, filters = {} }) => {
  const params = { entityType, sort }
  if (q && q.trim()) params.q = q.trim()
  Object.entries(filters).forEach(([key, ops]) => {
    Object.entries(ops || {}).forEach(([op, value]) => {
      if (value === '' || value === null || value === undefined) return
      params[op === 'eq' ? `filter.${key}` : `filter.${key}.${op}`] = value
    })
  })
  return params
}

export const recordsAPI = {
  /** filters: { fieldKey: { eq | contains | gte | lte: value } } */
  list: ({ page = 0, size = 20, ...query }) => data(crmClient.get('/services/records', { params: { ...listParams(query), page, size } })),
  /** CSV of the list query; columns = field keys in order. Resolves to { blob, filename }. */
  exportCsv: async ({ columns, ...query }) => {
    const params = listParams(query)
    if (columns?.length) params.columns = columns.join(',')
    const response = await crmClient.get('/services/records/export', { params, responseType: 'blob' })
    // named here: through a cross-origin gateway Content-Disposition isn't readable unless exposed
    const date = new Date().toISOString().slice(0, 10)
    return { blob: response.data, filename: `${query.entityType.toLowerCase()}-${date}.csv` }
  },
  /** Kanban columns: { groupBy, groupByLabel, sumField, sumFieldLabel, columns: [{ value, label, total, sum, items }] } */
  board: ({ groupBy, sumField, perColumn = 50, ...query }) => {
    const params = { ...listParams(query), groupBy, perColumn }
    if (sumField) params.sumField = sumField
    return data(crmClient.get('/services/records/board', { params }))
  },
  /** Change only the given fields; null clears one. */
  patch: (recordId, changes, lockVersion) => data(crmClient.patch(`/services/records/${recordId}`, { data: changes, lockVersion })),
  /** Same changes on up to 200 records → { succeeded, failed: [{ id, error }] } */
  bulkUpdate: (ids, changes) => data(crmClient.post('/services/records/bulk-update', { ids, changes })),
  bulkDelete: (ids) => data(crmClient.post('/services/records/bulk-delete', { ids })),
  /**
   * One batch (≤ 500) of mapped rows: { formCode, rows: [{ fieldKey: text }], dryRun, startRow, mode, matchField }.
   * mode: CREATE (default) | UPDATE (matching rows update the existing record) | SKIP (matching rows are left out)
   */
  importRows: (body) => data(crmClient.post('/services/records/import', body)),
  get: (recordId) => data(crmClient.get(`/services/records/${recordId}`)),
  /** Merges the duplicate into this record (it's deleted) → { record, activities, tasks, emails, files, links } */
  merge: (recordId, sourceId, useSource = []) => data(crmClient.post(`/services/records/${recordId}/merge`, { sourceId, useSource })),
  /** Existing records with the same email or phone → [{ record: { id, entityType, title }, matchedOn: [labels] }] */
  duplicates: (entityType, recordData, excludeId) => data(crmClient.post('/services/records/duplicates', { entityType, data: recordData, excludeId })),
  /** Records linking here through LOOKUP fields: [{ entityType, fieldKey, fieldLabel, formCode, total, items }] */
  related: (recordId, limit = 10) => data(crmClient.get(`/services/records/${recordId}/related`, { params: { limit } })),
  /** Children per parent through a link field: { [parentId]: count } (tree view). */
  childCounts: (entityType, field, ids) => data(crmClient.get('/services/records/child-counts', { params: { entityType, field, ids: ids.join(',') } })),
  create: (body) => data(crmClient.post('/services/records', body)),
  update: (recordId, body) => data(crmClient.put(`/services/records/${recordId}`, body)),
  remove: (recordId) => data(crmClient.delete(`/services/records/${recordId}`)),
}

/** Use-case templates (admins): gallery, install into this workspace, remove their sample data. */
export const templatesAPI = {
  list: () => data(crmClient.get('/services/templates')),
  get: (key) => data(crmClient.get(`/services/templates/${key}`)),
  /** → { installId, created: { recordTypes, roles, views, dashboards, automations, sampleRecords }, skipped, firstEntityType } */
  install: (key, includeSampleData) => data(crmClient.post(`/services/templates/${key}/install`, { includeSampleData })),
  removeSampleData: (installId) => data(crmClient.post(`/services/templates/installs/${installId}/remove-sample-data`)),
}

/** A record's timeline: { items: [{ kind, source, actor, createdOn, changes, body, … }], refs, page, hasMore } */
export const activityAPI = {
  list: (recordId, page = 0, size = 20) => data(crmClient.get(`/services/records/${recordId}/activity`, { params: { page, size } })),
  addNote: (recordId, body) => data(crmClient.post(`/services/records/${recordId}/notes`, { body })),
  editNote: (recordId, noteId, body) => data(crmClient.put(`/services/records/${recordId}/notes/${noteId}`, { body })),
  deleteNote: (recordId, noteId) => data(crmClient.delete(`/services/records/${recordId}/notes/${noteId}`)),
}

export const viewsAPI = {
  list: (entityType) => data(crmClient.get('/services/views', { params: { entityType } })),
  /** body: { entityType, name, shared, config: { columns, filters, sort, q } } */
  create: (body) => data(crmClient.post('/services/views', body)),
  update: (viewId, body) => data(crmClient.put(`/services/views/${viewId}`, body)),
  remove: (viewId) => data(crmClient.delete(`/services/views/${viewId}`)),
}

export const dashboardsAPI = {
  list: () => data(crmClient.get('/services/dashboards')),
  get: (id) => data(crmClient.get(`/services/dashboards/${id}`)),
  /** body: { name, isDefault, widgets: [widget definitions] } */
  create: (body) => data(crmClient.post('/services/dashboards', body)),
  update: (id, body) => data(crmClient.put(`/services/dashboards/${id}`, body)),
  remove: (id) => data(crmClient.delete(`/services/dashboards/${id}`)),
  /** One widget's numbers: { measureLabel, measureType, value, previousValue, series, points, rows } */
  widgetData: (query) => data(crmClient.post('/services/dashboards/widget-data', query)),
}

export const automationsAPI = {
  list: (entityType) => data(crmClient.get('/services/automations', { params: { entityType } })),
  get: (id) => data(crmClient.get(`/services/automations/${id}`)),
  create: (body) => data(crmClient.post('/services/automations', body)),
  update: (id, body) => data(crmClient.put(`/services/automations/${id}`, body)),
  setEnabled: (id, enabled) => data(crmClient.patch(`/services/automations/${id}`, { enabled })),
  remove: (id) => data(crmClient.delete(`/services/automations/${id}`)),
  runs: (id, limit = 50) => data(crmClient.get(`/services/automations/${id}/runs`, { params: { limit } })),
}

/** The signed-in member's own notifications in the current tenant. */
export const notificationsAPI = {
  list: (size = 20) => data(crmClient.get('/services/notifications', { params: { size } })),
  read: (id) => data(crmClient.post(`/services/notifications/${id}/read`)),
  readAll: () => data(crmClient.post('/services/notifications/read-all')),
}

export const rolesAPI = {
  list: () => data(crmClient.get('/services/tenant/roles')),
  get: (id) => data(crmClient.get(`/services/tenant/roles/${id}`)),
  create: (body) => data(crmClient.post('/services/tenant/roles', body)),
  update: (id, body) => data(crmClient.put(`/services/tenant/roles/${id}`, body)),
  remove: (id) => data(crmClient.delete(`/services/tenant/roles/${id}`)),
  /** The caller's effective permissions in the current tenant. */
  mine: () => data(crmClient.get('/services/permissions/me')),
}

/** Files on a record. Downloads go through the API (they need the sign-in), as blobs. */
export const filesAPI = {
  list: (recordId) => data(crmClient.get(`/services/records/${recordId}/files`)),
  upload: (recordId, file, onUploadProgress) => {
    const body = new FormData()
    body.append('file', file)
    // the client's default JSON content type would make axios serialize the form as JSON
    return data(crmClient.post(`/services/records/${recordId}/files`, body, { onUploadProgress, headers: { 'Content-Type': 'multipart/form-data' } }))
  },
  /** → Blob; preview = true serves PDFs and images inline (only those types) */
  content: (recordId, fileId, preview = false) =>
    crmClient.get(`/services/records/${recordId}/files/${fileId}/content`, { params: { preview }, responseType: 'blob' }).then((r) => r.data),
  remove: (recordId, fileId) => data(crmClient.delete(`/services/records/${recordId}/files/${fileId}`)),
}

/** Document templates (admins manage, everyone uses): HTML with {{placeholders}} → PDF */
export const documentTemplatesAPI = {
  list: (entityType) => data(crmClient.get('/services/document-templates', { params: { entityType } })),
  get: (id) => data(crmClient.get(`/services/document-templates/${id}`)),
  create: (body) => data(crmClient.post('/services/document-templates', body)),
  update: (id, body) => data(crmClient.put(`/services/document-templates/${id}`, body)),
  remove: (id) => data(crmClient.delete(`/services/document-templates/${id}`)),
  /** The template being edited, filled in from a record → PDF Blob (nothing saved) */
  previewDraft: (recordId, body) => blob(crmClient.post('/services/document-templates/preview', body, { params: { recordId }, responseType: 'blob' })),
}

/** PDFs made from templates on a record */
export const documentsAPI = {
  /** → PDF Blob, not saved */
  preview: (recordId, templateId) => blob(crmClient.get(`/services/records/${recordId}/documents/preview`, { params: { templateId }, responseType: 'blob' })),
  /** Saves the PDF to the record's files → the attachment */
  generate: (recordId, templateId) => data(crmClient.post(`/services/records/${recordId}/documents`, { templateId })),
}

/** E-signatures on a record's PDFs */
export const signaturesAPI = {
  list: (recordId) => data(crmClient.get(`/services/records/${recordId}/signatures`)),
  /** body: { attachmentId, signers: [{ name, email }], message, inOrder, expiresInDays } → request (signers[].signingUrl when email is off) */
  send: (recordId, body) => data(crmClient.post(`/services/records/${recordId}/signatures`, body)),
  cancel: (recordId, requestId) => data(crmClient.post(`/services/records/${recordId}/signatures/${requestId}/cancel`)),
  remind: (recordId, requestId) => data(crmClient.post(`/services/records/${recordId}/signatures/${requestId}/remind`)),
}

/** The signing page: no sign-in, the link's token (from its #fragment) is the key */
export const publicSignAPI = {
  open: (token) => data(crmClient.post('/public/sign/open', { token })),
  /** → PDF Blob */
  document: (token) => blob(crmClient.post('/public/sign/document', { token }, { responseType: 'blob' })),
  sign: (token, name, consent) => data(crmClient.post('/public/sign/sign', { token, name, consent })),
  decline: (token, reason) => data(crmClient.post('/public/sign/decline', { token, reason })),
}

/** How replies to record emails come back, and members' own Gmail / Outlook connections */
export const emailRepliesAPI = {
  /** → { replyAddress, webhook, webhookProviders, imap, mailboxProviders, comingSoon } (what's set up; no secrets) */
  status: () => data(crmClient.get('/services/email-replies/status')),
  /** → { available: ['GOOGLE', 'MICROSOFT'], mine: [{ provider, email, status, error, syncedUntil }] } */
  mailboxes: () => data(crmClient.get('/services/mail-connections')),
  /** → { url } to send the browser to */
  /** purpose: MAIL (replies) or CALENDAR → { url } */
  connect: (provider, purpose = 'MAIL') => data(crmClient.post(`/services/mail-connections/${provider.toLowerCase()}/start`, null, { params: { purpose } })),
  disconnect: (provider) => data(crmClient.delete(`/services/mail-connections/${provider.toLowerCase()}`)),
}

/** Send an email from a record → { status: SENT | FAILED, error, … } (logged on the timeline either way) */
export const emailsAPI = {
  send: (recordId, body) => data(crmClient.post(`/services/records/${recordId}/emails`, body)),
}

export const tasksAPI = {
  /** { scope: MINE | CREATED | ALL, status: OPEN | DONE, recordId, page, size } → { items, totalItems } */
  list: (params) => data(crmClient.get('/services/tasks', { params })),
  /** The caller's { open, overdue, dueToday } */
  summary: () => data(crmClient.get('/services/tasks/summary')),
  /** body: { title, notes, dueAt (local ISO), remindMinutesBefore, assigneeUserId, recordId } */
  create: (body) => data(crmClient.post('/services/tasks', body)),
  update: (id, body) => data(crmClient.put(`/services/tasks/${id}`, body)),
  complete: (id) => data(crmClient.post(`/services/tasks/${id}/complete`)),
  reopen: (id) => data(crmClient.post(`/services/tasks/${id}/reopen`)),
  remove: (id) => data(crmClient.delete(`/services/tasks/${id}`)),
}

/** Push notifications on the device the app runs on */
export const pushAPI = {
  /** → { enabled, publicKey, devices } */
  config: () => data(crmClient.get('/services/push/config')),
  /** sub: PushSubscription.toJSON() */
  subscribe: (sub) => data(crmClient.post('/services/push/subscriptions', sub)),
  unsubscribe: (endpoint) => data(crmClient.delete('/services/push/subscriptions', { params: { endpoint } })),
  /** → { sent } */
  test: () => data(crmClient.post('/services/push/test')),
}

/** Summary reports: definition { entityType, filters, q, groupBy: { field, interval }, splitBy, measures: [{ fn, field }], sort, descending } */
export const reportsAPI = {
  /** → { columns: [{ label, measure, fieldType }], rows: [{ groups: [{ value, label }], values }], totals, truncated } */
  run: (definition) => data(crmClient.post('/services/reports/run', definition)),
  runCsv: (definition) => blob(crmClient.post('/services/reports/run/csv', definition, { responseType: 'blob' })),
  list: () => data(crmClient.get('/services/reports')),
  get: (id) => data(crmClient.get(`/services/reports/${id}`)),
  /** body: { name, definition, shared, schedule: NONE | WEEKLY | MONTHLY, recipientIds } */
  create: (body) => data(crmClient.post('/services/reports', body)),
  update: (id, body) => data(crmClient.put(`/services/reports/${id}`, body)),
  remove: (id) => data(crmClient.delete(`/services/reports/${id}`)),
  runSaved: (id) => data(crmClient.get(`/services/reports/${id}/run`)),
  csv: (id) => blob(crmClient.get(`/services/reports/${id}/csv`, { responseType: 'blob' })),
}

/** Sales forecast per month or quarter */
export const forecastAPI = {
  /** → { entityType: { amountField, closeDateField, stageField, ownerField, probabilities, won, lost } } */
  settings: () => data(crmClient.get('/services/forecast/settings')),
  saveSettings: (entityType, settings) => data(crmClient.put(`/services/forecast/settings/${entityType}`, settings)),
  /** params: { entityType, period: MONTH | QUARTER, date } */
  get: (params) => data(crmClient.get('/services/forecast', { params })),
  /** body: { entityType, period, periodStart, targets: [{ userId | null, amount | null }] } */
  saveTargets: (body) => data(crmClient.put('/services/forecast/targets', body)),
}

/** Mass email (admins). body: { name, entityType, emailField, filters, q, subject, body } */
export const campaignsAPI = {
  list: () => data(crmClient.get('/services/campaigns')),
  get: (id) => data(crmClient.get(`/services/campaigns/${id}`)),
  create: (body) => data(crmClient.post('/services/campaigns', body)),
  update: (id, body) => data(crmClient.put(`/services/campaigns/${id}`, body)),
  remove: (id) => data(crmClient.delete(`/services/campaigns/${id}`)),
  /** → { matching, withEmail, unsubscribed, duplicates, sample: [{ email, record }], dailyLimit } */
  preview: (id) => data(crmClient.get(`/services/campaigns/${id}/preview`)),
  test: (id) => data(crmClient.post(`/services/campaigns/${id}/test`)),
  start: (id) => data(crmClient.post(`/services/campaigns/${id}/start`)),
  pause: (id) => data(crmClient.post(`/services/campaigns/${id}/pause`)),
  resume: (id) => data(crmClient.post(`/services/campaigns/${id}/resume`)),
  cancel: (id) => data(crmClient.post(`/services/campaigns/${id}/cancel`)),
  recipients: (id, status) => data(crmClient.get(`/services/campaigns/${id}/recipients`, { params: { status } })),
  suppressions: () => data(crmClient.get('/services/email-suppressions')),
  suppress: (email) => data(crmClient.post('/services/email-suppressions', { email })),
  allow: (email) => data(crmClient.delete('/services/email-suppressions', { params: { email } })),
}

/** Public: the unsubscribe link (no sign-in) */
export const unsubscribeAPI = {
  info: (token) => data(crmClient.get(`/public/unsubscribe/${encodeURIComponent(token)}`)),
  confirm: (token) => data(crmClient.post(`/public/unsubscribe/${encodeURIComponent(token)}`)),
}

/** Guided process (per record type) and approvals */
export const processAPI = {
  /** → { entityType: { stageField, transitions, checklists, approvals } } */
  all: () => data(crmClient.get('/services/process')),
  save: (entityType, settings) => data(crmClient.put(`/services/process/${entityType}`, settings)),
  remove: (entityType) => data(crmClient.delete(`/services/process/${entityType}`)),
  /** → { stageField, stage, stages, next, checklist, pending } or '' when the type has no process */
  path: (recordId) => data(crmClient.get(`/services/records/${recordId}/process`)),
  tick: (recordId, item, done) => data(crmClient.put(`/services/records/${recordId}/checklist`, { item, done })),
}

export const approvalsAPI = {
  ask: (recordId, changes, note) => data(crmClient.post(`/services/records/${recordId}/approvals`, { changes, note })),
  ofRecord: (recordId) => data(crmClient.get(`/services/records/${recordId}/approvals`)),
  /** box: inbox (waiting for me) | mine (I asked) */
  list: (box = 'inbox') => data(crmClient.get('/services/approvals', { params: { box } })),
  approve: (id, note) => data(crmClient.post(`/services/approvals/${id}/approve`, { note })),
  reject: (id, note) => data(crmClient.post(`/services/approvals/${id}/reject`, { note })),
  cancel: (id) => data(crmClient.post(`/services/approvals/${id}/cancel`)),
}

/** WhatsApp (the workspace's own number through Meta's Cloud API) */
export const whatsappAPI = {
  /** → { connected, displayPhone, webhookUrl, verifyToken, defaultCountryCode, contacts, createType, status, lastError } */
  account: () => data(crmClient.get('/services/whatsapp/account')),
  /** body: { phoneNumberId, wabaId, accessToken, appSecret, defaultCountryCode, contacts: [{ entityType, phoneField, nameField }], createType } */
  connect: (body) => data(crmClient.put('/services/whatsapp/account', body)),
  disconnect: () => data(crmClient.delete('/services/whatsapp/account')),
  /** → [{ name, language, category, body, variables }] */
  templates: () => data(crmClient.get('/services/whatsapp/templates')),
  /** → { number, windowOpen, windowClosesOn, messages } */
  thread: (recordId) => data(crmClient.get(`/services/records/${recordId}/whatsapp`)),
  /** body: { text } or { template, language, params } */
  send: (recordId, body) => data(crmClient.post(`/services/records/${recordId}/whatsapp`, body)),
  unmatched: () => data(crmClient.get('/services/whatsapp/unmatched')),
  link: (messageId, recordId) => data(crmClient.post(`/services/whatsapp/messages/${messageId}/link`, { recordId })),
}

/** Meetings and calls; times are local ISO like tasks */
export const meetingsAPI = {
  /** A record's meetings and calls, newest first */
  ofRecord: (recordId) => data(crmClient.get('/services/meetings', { params: { recordId } })),
  get: (id) => data(crmClient.get(`/services/meetings/${id}`)),
  /** body: { kind: MEETING | CALL, title, startsAt, endsAt, recordId, location, videoLink, ownerUserId, memberUserIds, guestEmails,
   *  notes, status, callDirection, callResult, remindMinutesBefore, sendInvites } */
  create: (body) => data(crmClient.post('/services/meetings', body)),
  update: (id, body) => data(crmClient.put(`/services/meetings/${id}`, body)),
  /** status: DONE | CANCELLED | PLANNED, outcome: added to the notes */
  setStatus: (id, status, outcome) => data(crmClient.post(`/services/meetings/${id}/status`, { status, outcome })),
  remove: (id) => data(crmClient.delete(`/services/meetings/${id}`)),
  /**
   * Before saving: body { startsAt, endsAt, ownerUserId, memberUserIds, meetingId, days } →
   * { conflicts: [{ user, start, end, allDay, title (yours only), source: CRM | GOOGLE | MICROSOFT }],
   *   suggestions: [{ start, end }], people: [{ user, calendarConnected, calendarProvider, calendarError }], workingHours }
   */
  availability: (body) => data(crmClient.post('/services/meetings/availability', body)),
}

/**
 * { from, to (local ISO), userId | team } → { meetings, tasks, people, external: [{ userId, start, end, allDay, title (yours
 * only), provider }], calendars: [{ userId, connected, provider, error }] }
 */
export const calendarAPI = {
  get: (params) => data(crmClient.get('/services/calendar', { params })),
}

/** Admins: personal-data requests and the workspace export (ZIPs resolve to Blobs). */
export const privacyAPI = {
  /** q = an email or a phone number → [{ record, matchedOn }] */
  search: (q) => data(crmClient.get('/services/tenant/privacy/search', { params: { q } })),
  exportPerson: (recordIds, reason) =>
    crmClient.post('/services/tenant/privacy/export', { recordIds, reason }, { responseType: 'blob' }).then((r) => r.data),
  /** Permanent. → { records, activities, tasks, emails, files, notifications } */
  erasePerson: (recordIds, reason) => data(crmClient.post('/services/tenant/privacy/erase', { recordIds, reason, confirm: 'ERASE' })),
  log: () => data(crmClient.get('/services/tenant/privacy/log')),
  exportWorkspace: () => crmClient.get('/services/tenant/export', { responseType: 'blob', timeout: 0 }).then((r) => r.data),
}

/** Admins: API keys for integrations. create → { apiKey, key } (the key is shown once) */
export const apiKeysAPI = {
  list: () => data(crmClient.get('/services/tenant/api-keys')),
  create: (name, access) => data(crmClient.post('/services/tenant/api-keys', { name, access })),
  revoke: (id) => data(crmClient.delete(`/services/tenant/api-keys/${id}`)),
}

/** Admins: webhooks. body: { name, url, events: [record.created|updated|deleted], entityTypes, enabled } */
export const webhooksAPI = {
  list: () => data(crmClient.get('/services/tenant/webhooks')),
  create: (body) => data(crmClient.post('/services/tenant/webhooks', body)),
  update: (id, body) => data(crmClient.put(`/services/tenant/webhooks/${id}`, body)),
  remove: (id) => data(crmClient.delete(`/services/tenant/webhooks/${id}`)),
  test: (id) => data(crmClient.post(`/services/tenant/webhooks/${id}/test`)),
  deliveries: (id) => data(crmClient.get(`/services/tenant/webhooks/${id}/deliveries`, { params: { limit: 50 } })),
}

/** Admins: public web forms. body: { formCode, title, intro, fields, defaults, successMessage, redirectUrl, dailyLimit, enabled } */
export const webFormsAPI = {
  list: () => data(crmClient.get('/services/tenant/web-forms')),
  create: (body) => data(crmClient.post('/services/tenant/web-forms', body)),
  update: (id, body) => data(crmClient.put(`/services/tenant/web-forms/${id}`, body)),
  newLink: (id) => data(crmClient.post(`/services/tenant/web-forms/${id}/new-link`)),
  remove: (id) => data(crmClient.delete(`/services/tenant/web-forms/${id}`)),
}

/** The public form page: no sign-in, the key in the address is the form. */
export const publicFormsAPI = {
  get: (key) => data(crmClient.get(`/public/web-forms/${encodeURIComponent(key)}`, { skipAuthRedirect: true })),
  submit: (key, body) => data(crmClient.post(`/public/web-forms/${encodeURIComponent(key)}`, body, { skipAuthRedirect: true })),
}

/** Email templates: everyone lists and fills them in; admins manage them. */
export const emailTemplatesAPI = {
  list: (entityType) => data(crmClient.get('/services/email-templates', { params: { entityType } })),
  create: (body) => data(crmClient.post('/services/email-templates', body)),
  update: (id, body) => data(crmClient.put(`/services/email-templates/${id}`, body)),
  remove: (id) => data(crmClient.delete(`/services/email-templates/${id}`)),
  /** → { subject, body } filled in for the record */
  render: (id, recordId) => data(crmClient.get(`/services/email-templates/${id}/render`, { params: { recordId } })),
}
